import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, ArrowLeft, Check, Heart } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { StarRating } from "../components/StarRating";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isWishlisted, toggle } = useWishlist();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, average: 0 });
  const [rRating, setRRating] = useState(0);
  const [rText, setRText] = useState("");
  const [rSubmitting, setRSubmitting] = useState(false);

  const loadReviews = () => api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    if (product?.name) document.title = `${product.name} — Monster Truck Lord`;
  }, [product]);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || null);
      setColor(r.data.colors?.[0] || null);
    }).catch(() => setNotFound(true));
    loadReviews();
  }, [id]);

  if (notFound) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-center text-zinc-400">Product not found. <Link to="/shop" className="text-[#FF3B30] underline">Back to shop</Link></div>;
  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Loading…</div>;

  const needsSize = product.sizes && product.sizes.length > 0;
  const hasColors = product.colors && product.colors.length > 0;
  const colorImages = product.color_images || {};
  const sizePrices = product.size_prices || {};
  const effectivePrice = (size && sizePrices[size] != null) ? sizePrices[size] : product.price;
  const hasVariedPrices = Object.keys(sizePrices).length > 0 && new Set(Object.values(sizePrices)).size > 1;

  const pickColor = (c) => {
    setColor(c);
    const idx = product.images?.indexOf(colorImages[c]);
    if (idx != null && idx >= 0) setActiveImg(idx);
  };

  const handleAdd = () => {
    if (needsSize && !size) { toast.error("Pick a size first"); return; }
    if (hasColors && !color) { toast.error("Pick a color first"); return; }
    addItem(product, size, qty, effectivePrice, color);
    toast.success(`${product.name} added to cart`);
  };

  const handleBuy = () => { handleAdd(); navigate("/cart"); };

  const wished = isWishlisted(product.id);
  const handleWish = () => {
    if (!user) { toast.error("Log in to save favorites"); navigate("/login"); return; }
    toggle(product.id);
    toast.success(wished ? "Removed from wishlist" : "Saved to wishlist");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Log in to leave a review"); navigate("/login"); return; }
    if (rRating < 1) { toast.error("Pick a star rating"); return; }
    setRSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, { rating: rRating, text: rText });
      toast.success("Thanks for your review!");
      setRText(""); setRRating(0);
      loadReviews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setRSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="product-detail">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors mb-8"><ArrowLeft size={16} /> Back</button>
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square bg-zinc-900 border border-zinc-800 overflow-hidden">
            <img src={product.images?.[activeImg]} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-20 border overflow-hidden ${activeImg === i ? "border-[#FF3B30]" : "border-zinc-800"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EAB308]">{product.category}</span>
          <div className="flex items-start justify-between gap-4 mt-2">
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none" data-testid="product-name">{product.name}</h1>
            <button onClick={handleWish} data-testid="wishlist-toggle-detail" aria-label="Toggle wishlist"
              className="shrink-0 p-3 border border-zinc-700 hover:border-[#FF3B30] transition-colors">
              <Heart size={20} className={wished ? "fill-[#FF3B30] text-[#FF3B30]" : "text-white"} />
            </button>
          </div>
          <div className="font-display text-4xl text-[#FF3B30] mt-4" data-testid="product-price">
            {hasVariedPrices && !size ? "From " : ""}${effectivePrice.toFixed(2)}
          </div>
          {reviews.count > 0 && (
            <a href="#reviews" className="flex items-center gap-2 mt-3" data-testid="detail-rating-summary">
              <StarRating value={reviews.average} size={16} />
              <span className="text-sm text-zinc-400">{reviews.average} · {reviews.count} review{reviews.count > 1 ? "s" : ""}</span>
            </a>
          )}
          <p className="text-zinc-300 leading-relaxed mt-6">{product.description}</p>

          {hasColors && (
            <div className="mt-8" data-testid="color-selector">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">Color{color ? `: ${color}` : ""}</h4>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => (
                  <button key={c} data-testid={`color-${c}`} onClick={() => pickColor(c)}
                    className={`px-4 py-2.5 text-sm font-bold border transition-colors duration-200 ${color === c ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-300 hover:border-white"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {needsSize && (
            <div className="mt-8">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">Size</h4>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                    className={`px-4 py-3 text-sm font-bold border transition-colors duration-200 flex flex-col items-center gap-0.5 ${size === s ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-300 hover:border-white"}`}>
                    <span>{s}</span>
                    {sizePrices[s] != null && <span className="text-[10px] opacity-70">${sizePrices[s].toFixed(2)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">Quantity</h4>
            <div className="flex items-center border border-zinc-700 w-fit">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-lg hover:bg-zinc-800 transition-colors" data-testid="qty-minus">−</button>
              <span className="px-6 font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 text-lg hover:bg-zinc-800 transition-colors" data-testid="qty-plus">+</button>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button onClick={handleAdd} data-testid="add-to-cart-button" className="flex-1 flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
              <ShoppingCart size={18} /> Add To Cart
            </button>
            <button onClick={handleBuy} data-testid="buy-now-button" className="flex-1 flex items-center justify-center gap-2 border border-white text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
              Buy Now
            </button>
          </div>
          <p className="mt-6 flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest"><Check size={14} className="text-[#EAB308]" /> In stock — ships in 48 hours</p>
        </div>
      </div>

      <div id="reviews" className="mt-20 border-t border-zinc-800 pt-12" data-testid="reviews-section">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">Customer <span className="text-[#FF3B30]">Reviews</span></h2>
            {reviews.count > 0 && (
              <div className="flex items-center gap-3 mt-3">
                <StarRating value={reviews.average} size={18} />
                <span className="text-zinc-400 text-sm">{reviews.average} out of 5 · {reviews.count} review{reviews.count > 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-5">
            {reviews.reviews.length === 0 ? (
              <p className="text-zinc-500" data-testid="no-reviews">No reviews yet. Be the first to rep this gear.</p>
            ) : (
              reviews.reviews.map((rv) => (
                <div key={rv.id} className="border border-zinc-800 bg-zinc-900 p-5" data-testid={`review-${rv.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="font-bold uppercase tracking-tight text-sm">{rv.user_name}</span>
                      {rv.verified_buyer && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-400 border border-green-500/40 px-2 py-0.5" data-testid="verified-buyer-badge">
                          <Check size={10} /> Verified Buyer
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-zinc-600">{new Date(rv.created_at).toLocaleDateString()}</span>
                  </div>
                  <StarRating value={rv.rating} size={14} className="mt-2" />
                  {rv.text && <p className="text-zinc-300 text-sm mt-3 leading-relaxed">{rv.text}</p>}
                </div>
              ))
            )}
          </div>

          <div className="border border-zinc-800 bg-zinc-900 p-6 h-fit" data-testid="write-review-box">
            <h3 className="font-display text-xl uppercase tracking-tight mb-4">Write a Review</h3>
            {user ? (
              <form onSubmit={submitReview} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Your Rating</label>
                  <StarRating value={rRating} size={26} interactive onSelect={setRRating} />
                </div>
                <textarea rows="4" value={rText} onChange={(e) => setRText(e.target.value)} data-testid="review-text"
                  placeholder="Tell other Disciples what you think…"
                  className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white focus:border-[#FF3B30] outline-none transition-colors" />
                <button type="submit" disabled={rSubmitting} data-testid="submit-review-button"
                  className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-6 py-3 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
                  {rSubmitting ? "Posting…" : "Post Review"}
                </button>
              </form>
            ) : (
              <p className="text-sm text-zinc-400">
                <Link to="/login" className="text-[#FF3B30] font-bold">Log in</Link> to leave a review.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
